import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

// ---------- Cover Letter ----------
export type CoverLetterResult = {
  letter: string;
  hookSentence: string;
  keyMatches: string[];
};

const COVER_LETTER_TOOL = {
  name: "save_cover_letter",
  description:
    "Generate a tailored cover letter for the candidate, given the JD and tone. Reference real items from the candidate profile — do not invent.",
  parameters: {
    type: "object",
    properties: {
      letter: {
        type: "string",
        description:
          "Full cover letter text including salutation and signoff. Use plain text with line breaks. ~250-400 words.",
      },
      hookSentence: {
        type: "string",
        description: "The opening hook in one sentence.",
      },
      keyMatches: {
        type: "array",
        items: { type: "string" },
        description: "3-5 specific candidate strengths that map to the JD.",
      },
    },
    required: ["letter", "hookSentence", "keyMatches"],
    additionalProperties: false,
  },
};

export const generateCoverLetter = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      jdText: string;
      company: string;
      role: string;
      tone: "corporate" | "startup" | "creative";
    }) => {
      if (!input?.jdText || input.jdText.length < 20)
        throw new Error("jdText is required (min 20 chars)");
      if (!input.company?.trim()) throw new Error("company is required");
      if (!input.role?.trim()) throw new Error("role is required");
      if (!["corporate", "startup", "creative"].includes(input.tone))
        throw new Error("invalid tone");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    try {
      await enforceQuota(supabase, userId, CAPABILITIES.cover_letter);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    const { data: candidateRow } = await supabase
      .from("candidate_profiles")
      .select("verified_skills, chronological_experience, raw_resume_text")
      .eq("user_id", userId)
      .maybeSingle();

    const profileSummary = JSON.stringify(
      {
        skills: candidateRow?.verified_skills ?? [],
        experience: candidateRow?.chronological_experience ?? {},
      },
      null,
      2,
    ).slice(0, 12_000);

    const toneGuide = {
      corporate:
        "Formal, polished, third-paragraph structure. Use 'Dear Hiring Team' / 'Sincerely'.",
      startup:
        "Conversational, direct, first-person. Short paragraphs. Use 'Hi <company> team' / '— <name>'.",
      creative:
        "Distinctive opening. Show voice and craft. Use unconventional structure.",
    }[data.tone];

    let result: CoverLetterResult;
    try {
      result = await callAIStructured<CoverLetterResult>({
        messages: [
          {
            role: "system",
            content: `You are an elite career coach writing a cover letter. Tone: ${toneGuide} Reference only achievements present in the candidate profile. Do not invent companies, metrics, or projects.`,
          },
          {
            role: "user",
            content: `Company: ${data.company}\nRole: ${data.role}\n\nJob Description:\n${data.jdText}\n\nCandidate Profile:\n${profileSummary}`,
          },
        ],
        tool: COVER_LETTER_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    await supabase.from("generated_materials").insert({
      user_id: userId,
      type: "cover_letter",
      content: {
        ...result,
        company: data.company,
        role: data.role,
        tone: data.tone,
      } as unknown as never,
    });

    await incrementUsage(userId, CAPABILITIES.cover_letter);

    return result;
  });

// ---------- STAR Q&A ----------
export type STARResult = {
  situation: string;
  task: string;
  action: string;
  result: string;
};

const STAR_TOOL = {
  name: "save_star_answer",
  description:
    "Answer an employer's behavioural question using the STAR method, grounded in the candidate's real experience. Each section is 2-4 sentences.",
  parameters: {
    type: "object",
    properties: {
      situation: { type: "string", description: "Set the scene with real context." },
      task: { type: "string", description: "What needed to be accomplished." },
      action: {
        type: "string",
        description: "Specific actions YOU (the candidate) took.",
      },
      result: {
        type: "string",
        description: "Quantified outcome where possible.",
      },
    },
    required: ["situation", "task", "action", "result"],
    additionalProperties: false,
  },
};

export const generateSTARAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { question: string }) => {
    if (!input?.question || input.question.trim().length < 10)
      throw new Error("question is required (min 10 chars)");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    try {
      await enforceQuota(supabase, userId, CAPABILITIES.qa_answer);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    const { data: candidateRow } = await supabase
      .from("candidate_profiles")
      .select("chronological_experience, verified_skills")
      .eq("user_id", userId)
      .maybeSingle();

    const profileSummary = JSON.stringify(
      {
        skills: candidateRow?.verified_skills ?? [],
        experience: candidateRow?.chronological_experience ?? {},
      },
      null,
      2,
    ).slice(0, 12_000);

    let result: STARResult;
    try {
      result = await callAIStructured<STARResult>({
        messages: [
          {
            role: "system",
            content:
              "You are a career coach. Write a STAR-method answer using a real story from the candidate's actual experience. Be concrete and specific. Never invent companies or metrics not in the profile.",
          },
          {
            role: "user",
            content: `Question: ${data.question}\n\nCandidate Profile:\n${profileSummary}`,
          },
        ],
        tool: STAR_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    await supabase.from("generated_materials").insert({
      user_id: userId,
      type: "qa",
      content: {
        question: data.question,
        ...result,
      } as unknown as never,
    });

    await incrementUsage(userId, CAPABILITIES.qa_answer);

    return result;
  });
